/* eslint-disable @typescript-eslint/explicit-function-return-type */
/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import { isEmpty } from "lodash";

import * as rdUserProfileApi from "Services/RD/RDApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { BenefitNameEnum } from "Constants/BenefitConstants";
import { FilterValueFieldNames } from "Constants/FilterFields";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import { defaultListingPageSize } from "Constants/ProfileConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IClaimBenefitDataResult } from "Interfaces/Benefits/IClaimBenefitDataResult";
import { IClaimBenefitResult } from "Interfaces/Benefits/IClaimBenefitResult";
import { IExtendAzureBenefitCreditResult } from "Interfaces/Benefits/IExtendAzureBenefitCreditResult";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IM365BenefitDetailDataResult } from "Interfaces/Benefits/IM365BenefitDetailDataResult";
import { IProfileBenefitDataResult } from "Interfaces/Benefits/IProfileBenefitDataResult";
import { ICommentAddRequest } from "Interfaces/Comments/ICommentAddRequest";
import { ICommentAddResult } from "Interfaces/Comments/ICommentAddResult";
import { ICommentsGetResult } from "Interfaces/Comments/ICommentsGetResult";
import { IApiEmptyResult, IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IProgramProfileUpdateRequest } from "Interfaces/ProgramProfile/IProgramProfileUpdateRequest";
import { IProgramProfileUpdateResult } from "Interfaces/ProgramProfile/IProgramProfileUpdateResult";
import { IRDProgramProfile } from "Interfaces/RD/ProgramProfile/IRDProgramProfile";
import { IGetRDUserProfilesRequest } from "Interfaces/RD/UserProfile/IGetRDUserProfilesRequest";
import { IRDUserProfileGetResult } from "Interfaces/RD/UserProfile/IRDUserProfileGetResult";
import { IRDUserProfilesSearchResult } from "Interfaces/RD/UserProfile/IRDUserProfilesSearchResult";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import {
  addUserProfileCommentFail,
  addUserProfileCommentSuccess,
  addAzureBenefitDetailFail,
  addAzureBenefitDetailSuccess,
  claimRdBenefitFail,
  claimRdBenefitSuccess,
  downloadRDUserProfilesToCSVFail,
  downloadRDUserProfilesToCSVSuccess,
  extendAzureBenefitCreditFailure,
  extendAzureBenefitCreditSuccess,
  fetchM365BenefitDetailFailure,
  fetchM365BenefitDetailSuccess,
  fetchProgramProfileFail,
  fetchProgramProfileSuccess,
  fetchUserProfileCommentsFail,
  fetchUserProfileCommentsSuccess,
  fetchUserProfileFail,
  fetchUserProfileSuccess,
  getProfileBenefitsSuccess,
  profileBenefitsFail,
  searchRDUserProfilesFail,
  searchUserRDProfilesSuccess,
  updateM365BenefitDetailFailure,
  updateM365BenefitDetailSuccess,
  updateRDProgramProfileFail,
  updateRDProgramProfileSuccess,
  userProfileIsPrivateUpdateFail,
  userProfileIsPrivateUpdateSuccess,
  userProfilePrimaryEmailUpdateFail,
  userProfilePrimaryEmailUpdateSuccess,
  IRDAddUserProfileComment,
  IRDClaimBenefit,
  IRDDownloadToCSV,
  IRDExtendAzureBenefitCredit,
  IRDFetchM365BenefitDetail,
  IRDFetchProgramProfile,
  IRDFetchUserProfile,
  IRDFetchUserProfileComments,
  IRDGetProfileBenefits,
  IRDProfilesSearch,
  IRDUpdateM365BenefitDetail,
  IRDUpdateProgramProfile,
  IRDUserProfileIsPrivateUpdate,
  IRDUserProfilePrimaryEmailUpdate,
  IRDUpdateMS365BenefitDetail,
  fetchMS365BenefitDetailFailure,
  fetchMS365BenefitDetailSuccess,
  updateMS365BenefitDetailFailure,
  updateMS365BenefitDetailSuccess,
  IRDAddAzureBenefitDetail,
} from "./RDUserProfileAction";

/**
 * Updates the request body with filter values.
 * @param {IFilterValue[]} filterValues The list of filters.
 * @return {IGetRDUserProfilesRequest} The request to be sent.
 */
const updateFilterValues = (filterValues: IFilterValue[]): IGetRDUserProfilesRequest => {
  const requestBody: IGetRDUserProfilesRequest = {
    programStatusList: [],
    industryFocusList: [],
    technicalExpertiseList: [],
    clRegionList: [],
    countryRegionList: [],
    stateProvinceList: [],
    otherProgramMembershipList: [],
    awardPeriodStartDate: undefined,
    awardPeriodEndDate: undefined,
  };

  filterValues.forEach(function (item) {
    switch (item.fieldName) {
      case "programStatus":
        requestBody.programStatusList.push(item.value);
        break;
      case "industryFocus":
        requestBody.industryFocusList.push(item.value);
        break;
      case "technicalExpertise":
        requestBody.technicalExpertiseList.push(item.value);
        break;
      case "clRegion":
        requestBody.clRegionList.push(item.value);
        break;
      case "country":
        requestBody.countryRegionList.push(item.value);
        break;
      case "stateOrProvince":
        requestBody.stateProvinceList.push(item.value);
        break;
      case "otherProgramMembership":
        requestBody.otherProgramMembershipList.push(item.value);
        break;
      case FilterValueFieldNames.AWARD_PERIOD_START_DATE:
        requestBody.awardPeriodStartDate = new Date(item.value);
        break;
      case FilterValueFieldNames.AWARD_PERIOD_END_DATE:
        requestBody.awardPeriodEndDate = new Date(item.value);
        break;
      default:
        return;
    }
  });
  return requestBody;
};

/**
 * Generator function to async dispatch action
 * @generator
 * @param {ISearchUserProfiles} action To search user profile for given text.
 * @returns {void}
 */
function* handleRDSearchUserProfiles(action: IRDProfilesSearch): Generator {
  const failedMessage = "Failed to search the user profiles.";
  const requestBody = updateFilterValues(action.selectedUserProfilesFilterValues);
  requestBody.searchKey = action.searchText;
  requestBody.pageIndex = action.searchPage;
  requestBody.pageSize = action.pageSize;
  requestBody.sortColumn = action.sortColumn;
  requestBody.sortDescending = action.sortDescending;

  try {
    const { result, timeout }: IApiResult2<IRDUserProfilesSearchResult> | any = yield race({
      result: call(rdUserProfileApi.searchRDUserProfiles, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchRDUserProfilesFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfiles, filteredCount } = result.parsedBody as IRDUserProfilesSearchResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        // TODO: if null result, response code is 200 and "userProfile" returned.
        // If backend updated to instead return "userProfiles" we can clean this up some
        if (isEmpty(userProfiles)) {
          yield put(searchUserRDProfilesSuccess([], 1, defaultListingPageSize, 0));
        } else {
          yield put(
            searchUserRDProfilesSuccess(
              userProfiles,
              action.searchPage,
              action.pageSize,
              filteredCount
            )
          );
        }
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchRDUserProfilesFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchRDUserProfilesFail(failedMessage));
        break;
    }
  } catch (error) {
    // TODO: getting bad request and error returned when no users found and falling into this catch
    yield put(searchRDUserProfilesFail(error as string));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDDownloadToCSV} action To download user profiles.
 * @returns {void}
 */
function* handleRDUserProfileDownload(action: IRDDownloadToCSV) {
  const defaultFailedErrorMessage = "Failed to download user profiles";
  const requestBody = updateFilterValues(action.selectedUserProfilesFilterValues);
  requestBody.searchKey = action.searchText;

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.downloadRDUserProfiles, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(downloadRDUserProfilesToCSVFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const data = yield result.text();

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(downloadRDUserProfilesToCSVSuccess(data));
        break;
      case HttpStatusCode.BadRequest:
        yield put(downloadRDUserProfilesToCSVFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(downloadRDUserProfilesToCSVFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(downloadRDUserProfilesToCSVFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDFetchUserProfile} action To get user profile .
 * @returns {void}
 */
function* handleRDUserProfileFetch(action: IRDFetchUserProfile) {
  const defaultFailedErrorMessage = "Failed to fetch user profile";
  const { rdUserProfileId } = action;

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.fetchRDUserProfile, rdUserProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchUserProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfile } = result.parsedBody as IRDUserProfileGetResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchUserProfileSuccess(userProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchUserProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchUserProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchUserProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDFetchProgramProfile} action To get Program profile.
 * @returns {void}
 */
function* handleRDProgramProfileFetch(action: IRDFetchProgramProfile) {
  const defaultFailedErrorMessage = "Failed to fetch Program profile";
  const { programProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.getRDProgramProfile, programProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchProgramProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const programProfile = result.parsedBody as IRDProgramProfile;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchProgramProfileSuccess(programProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchProgramProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchProgramProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchProgramProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDFetchUserProfileComments} action To get user profile comments .
 * @returns {void}
 */
function* handleRDUserProfileCommentsFetch(action: IRDFetchUserProfileComments) {
  const defaultFailedErrorMessage = "Failed to fetch user profile comments";
  const { rdUserProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.fetchRDUserProfileComments, rdUserProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchUserProfileCommentsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfileComments } = result.parsedBody as ICommentsGetResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchUserProfileCommentsSuccess(userProfileComments));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchUserProfileCommentsFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchUserProfileCommentsFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchUserProfileCommentsFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDAddUserProfileComment} action To add user profile comment.
 * @returns {void}
 */
function* handleRDAddUserProfileComment(action: IRDAddUserProfileComment) {
  const defaultFailedErrorMessage = "Failed to add user profile comment";
  const { rdUserProfileId, textValue, createdBy } = action;
  const requestBody: ICommentAddRequest = {
    userProfileComment: {
      userProfileId: rdUserProfileId,
      text: textValue,
      createdBy,
      tenantName: TenantTypesEnum.RD,
    },
  };

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.addRDUserProfileComment, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(addUserProfileCommentFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfileId } = result.parsedBody as ICommentAddResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(addUserProfileCommentSuccess(userProfileId));
        break;
      case HttpStatusCode.BadRequest:
        yield put(addUserProfileCommentFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(addUserProfileCommentFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(addUserProfileCommentFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDUpdateProgramProfile} action To update program profile.
 * @returns {void}
 */
function* handleRDUpdateProgramProfile(action: IRDUpdateProgramProfile) {
  const defaultFailedErrorMessage = "Failed to update program profile";
  const { rdUserProfileId, programProfileStatus, modifiedBy } = action;
  const requestBody: IProgramProfileUpdateRequest = {
    programProfile: {
      userProfileId: rdUserProfileId,
      programProfileStatus: programProfileStatus,
      modifiedBy,
      tenantName: TenantTypesEnum.RD,
    },
  };

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.updateRDProgramProfile, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateRDProgramProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { programProfile } = result.parsedBody as IProgramProfileUpdateResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(updateRDProgramProfileSuccess(programProfile.programProfileStatusName));
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateRDProgramProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateRDProgramProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(updateRDProgramProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUpdateProgramProfile} action To claim program profile benefit.
 * @returns {void}
 */
function* handleRDClaimBenefit(action: IRDClaimBenefit): Generator {
  const failedMessage =
    "API call failed, please try again and let us know if issue is not resolved.";

  const benefitApiMap = {
    [BenefitNameEnum.GitHubPro]: rdUserProfileApi.claimUserProfileBenefitsGithubPro,
    [BenefitNameEnum.LinkedInLearning]: rdUserProfileApi.claimUserProfileBenefitsLinkedIn,
    [BenefitNameEnum.M365]: rdUserProfileApi.claimUserProfileBenefitsM365,
    [BenefitNameEnum.MS365]: rdUserProfileApi.claimUserProfileBenefitsMS365,
    [BenefitNameEnum.Teams]: rdUserProfileApi.claimUserProfileBenefitsTeams,
  };

  try {
    const apiCall = benefitApiMap[action.benefitType];
    const { result, timeout }: IApiResult2<IClaimBenefitDataResult> | any = yield race({
      result: call(apiCall, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(claimRdBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        const requestResult = result.parsedBody as IClaimBenefitResult;
        if (!requestResult.isSuccess) {
          yield put(claimRdBenefitFail(failedMessage));
        }
        yield put(claimRdBenefitSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(claimRdBenefitFail((result.parsedBody as IApiErrorResult).error.message));
      default:
        yield put(claimRdBenefitFail((result.parsedBody as IApiErrorResult).error.message));
    }
  } catch (error) {
    yield put(claimRdBenefitFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action to fetch a profiles benefits
 * @generator
 * @param {IRDGetProfileBenefits} action To fetch a profiles benefits.
 * @returns {void}
 */
function* handleRDGetProfileBenefits(action: IRDGetProfileBenefits): Generator {
  const defaultFailedErrorMessage = "Failed to fetch profile benefits";

  try {
    const { result, timeout }: IApiResult2<IProfileBenefitDataResult> | any = yield race({
      result: call(rdUserProfileApi.getProfileBenefits, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(profileBenefitsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(getProfileBenefitsSuccess(result.parsedBody));
        break;
      case HttpStatusCode.BadRequest:
        yield put(profileBenefitsFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(profileBenefitsFail(defaultFailedErrorMessage));
    }
  } catch (error) {
    yield put(profileBenefitsFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action to fetch the RD M365 Detail.
 * @generator
 * @param {IRDGetProfileBenefits} action To fetch a profiles benefits.
 * @returns {void}
 */
function* handleRDGetM365BenefitDetail(action: IRDFetchM365BenefitDetail): Generator {
  const failedMessage = "Failed to get O365 Benefit Details";

  try {
    const { result, timeout }: IApiResult2<IM365BenefitDetailDataResult> | any = yield race({
      result: call(rdUserProfileApi.getM365BenefitDetails, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchM365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(fetchM365BenefitDetailSuccess(result.parsedBody as IM365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchM365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(fetchM365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchM365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDFetchM365BenefitDetail} action To get the users Microsoft 365 Benefit Details.
 * @returns {void}
 */
function* handleRDGetMS365BenefitDetail(action: IRDFetchM365BenefitDetail) {
  const failedMessage = "Failed to get O365 Benefit Details";

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.getMS365BenefitDetails, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchMS365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(fetchMS365BenefitDetailSuccess(result.parsedBody as IMicrosoft365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          fetchMS365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(fetchMS365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(fetchMS365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDUpdateM365BenefitDetail} action To update/create the users Microsoft 365 Benefit Details.
 * @returns {void}
 */
function* handleRDUpdateMS365BenefitDetail(action: IRDUpdateMS365BenefitDetail) {
  const failedMessage = "Failed to update M365 Benefit Details";

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.updateMS365BenefitDetails, action.ms365BenefitDetail),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateMS365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        yield put(updateMS365BenefitDetailSuccess(result.parsedBody as IMicrosoft365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateMS365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(updateMS365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateMS365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action to update the M365 Benefit Detail.
 * @generator
 * @param {IRDGetProfileBenefits} action To fetch a profiles benefits.
 * @returns {void}
 */
function* handleRDUpdateM365BenefitDetail(action: IRDUpdateM365BenefitDetail): Generator {
  const failedMessage = "Failed to update M365 Benefit Details";

  try {
    const { result, timeout }: IApiResult2<IM365BenefitDetailDataResult> | any = yield race({
      result: call(rdUserProfileApi.updateM365BenefitDetails, action.m365BenefitDetail),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateM365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        yield put(updateM365BenefitDetailSuccess(result.parsedBody as IM365BenefitDetail));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateM365BenefitDetailFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(updateM365BenefitDetailFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateM365BenefitDetailFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDExtendAzureBenefitCredit} action To update/create the users M365 Benefit Details.
 * @returns {void}
 */
function* handleRDExtendAzureBenefitCredit(action: IRDExtendAzureBenefitCredit) {
  const failedMessage = "Failed to extend Azure Benefit Credit";

  try {
    const { result, timeout } = yield race({
      result: call(rdUserProfileApi.extendAzureBenefitCredit, action.credit, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateM365BenefitDetailFailure(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const requestResult = result.parsedBody as IExtendAzureBenefitCreditResult;
        if (!requestResult.success) {
          yield put(extendAzureBenefitCreditFailure(failedMessage));
        }
        yield put(extendAzureBenefitCreditSuccess(requestResult.capCredit));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          extendAzureBenefitCreditFailure((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(extendAzureBenefitCreditFailure(failedMessage));
        break;
    }
  } catch (error) {
    yield put(extendAzureBenefitCreditFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUserProfileDlLoad} action To remove dl.
 * @returns {void}
 */
function* handleRDUserProfileIsPrivateUpdate(action: IRDUserProfileIsPrivateUpdate) {
  const failedMessage = "Failed to set the is private flag for profile";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(
        rdUserProfileApi.updateUserProfileIsPrivate,
        action.userProfileId,
        action.isPrivate
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileIsPrivateUpdateFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileIsPrivateUpdateSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfileIsPrivateUpdateFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfileIsPrivateUpdateFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileIsPrivateUpdateFail(error));
  }
}

/**
 * Generator function to async dispatch action.
 * @generator
 * @param {IRDUserProfilePrimaryEmailUpdate} action The action to update the primary email.
 * @returns {void}
 */
function* handleRDUserProfilePrimaryEmailUpdate(action: IRDUserProfilePrimaryEmailUpdate) {
  const failedMessage = "Failed to update primary email";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(
        rdUserProfileApi.updateUserProfilePrimaryEmail,
        action.userProfileId,
        action.newEmail
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfilePrimaryEmailUpdateFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfilePrimaryEmailUpdateSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfilePrimaryEmailUpdateFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfilePrimaryEmailUpdateFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(
      userProfilePrimaryEmailUpdateFail(
        `Handle Update Primary Email API failed: ${error as string}`
      )
    );
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IRDAddAzureBenefitDetail} action To update program profile.
 * @returns {void}
 */
function* handleRDAddAzureBenefit(action: IRDAddAzureBenefitDetail) {
  const failedMessage = "Failed to add Azure Benefit ";
  try {
    const { result, timeout } = yield race({
      result: call(
        rdUserProfileApi.saveAzureBenefitClaim,
        action.azureBenefit,
        action.userProfileId
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(addAzureBenefitDetailFail(GENERIC_API_TIMEOUT_ERROR));
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const requestResult = result.parsedBody as boolean;
        if (!requestResult) {
          yield put(addAzureBenefitDetailFail(failedMessage));
        }
        yield put(addAzureBenefitDetailSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(addAzureBenefitDetailFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(addAzureBenefitDetailFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(addAzureBenefitDetailFail(failedMessage));
  }
}

export {
  handleRDClaimBenefit,
  handleRDSearchUserProfiles,
  handleRDUserProfileDownload,
  handleRDUserProfileFetch,
  handleRDUpdateProgramProfile,
  handleRDUserProfileCommentsFetch,
  handleRDAddUserProfileComment,
  handleRDGetProfileBenefits,
  handleRDGetM365BenefitDetail,
  handleRDUpdateM365BenefitDetail,
  handleRDGetMS365BenefitDetail,
  handleRDUpdateMS365BenefitDetail,
  handleRDProgramProfileFetch,
  handleRDExtendAzureBenefitCredit,
  handleRDUserProfileIsPrivateUpdate,
  handleRDUserProfilePrimaryEmailUpdate,
  handleRDAddAzureBenefit,
};
